import React, { useRef, useState } from "react";
import * as XLSX from "xlsx";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import {
  useAddAttendee,
  useCheckAttendeeDuplicateEmailNationalIdAPONumber,
  useEditAttendee,
  useGetEventAttendees,
} from "../../hooks/useAttendee";
import cityService from "../../services/cityService";
import countryService from "../../services/countryService";
import { toast } from "react-toastify";
import {
  useAddDoctorsMasterData,
  useEditDoctorsMasterData,
} from "../../hooks/useDoctorMasterData";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import doctorMasterDataService from "../../services/doctorMasterDataService";
import { CircularProgress } from "@mui/material";

export default function ImportAttendees({ eventId }) {
  const { mutate: addAttendee } = useAddAttendee();
  const { mutateAsync: editAttendee } = useEditAttendee();
  const { mutateAsync: checkDuplicate } =
    useCheckAttendeeDuplicateEmailNationalIdAPONumber();
  const { data: eventAttendees } = useGetEventAttendees(eventId);
  const { mutateAsync: addDoctorMasterData } = useAddDoctorsMasterData();
  const { mutateAsync: editDoctorMasterData } = useEditDoctorsMasterData();

  const fileInputRef = useRef(null);
  const [isImporting, setIsImporting] = useState(false);

  const handleClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleDownloadTemplate = () => {
    // Create template headers
    const headers = [
      "First Name",
      "Family Name",
      "National ID",
      "Email Address",
      "Phone Number",
      "Country",
      "City",
      "Organization",
      "Specialty",
      "Medical License",
      "Industry Area",
      "APO Number",
    ];

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([headers]);

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, "Attendees Template");

    // Save file
    XLSX.writeFile(wb, "create_attendees_template.xlsx");
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setIsImporting(true);
      try {
        const reader = new FileReader();
        reader.onload = async (e) => {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);

          let successCount = 0;
          let hasErrors = false;

          // ✅ Split data into chunks of 10
          const chunkSize = 10;
          const chunks = [];
          for (let i = 0; i < jsonData.length; i += chunkSize) {
            chunks.push(jsonData.slice(i, i + chunkSize));
          }

          // ✅ Process each chunk sequentially
          for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
            const chunk = chunks[chunkIndex];

            for (const row of chunk) {
              try {
                const requiredFields = [
                  "First Name",
                  "Family Name",
                  "National ID",
                  "Email Address",
                  "Phone Number",
                  "Country",
                  "City",
                  "Organization",
                  "Specialty",
                  "Medical License",
                ];

                for (const field of requiredFields) {
                  if (!row[field]) {
                    toast.error(`Missing required field: ${field}`);
                  }
                }

                const [cityId, countryId] = await Promise.all([
                  cityService.getIdByName(row["City"]),
                  countryService.getIdByName(row["Country"]),
                ]);

                if (!cityId || !countryId) {
                  hasErrors = true;
                  toast.error(
                    `Invalid City or Country for ${row["Email Address"]}`
                  );
                  continue;
                }

                const nationalId = String(row["National ID"]);
                const email = String(row["Email Address"]);
                const apoNumber = row["APO Number"]
                  ? String(row["APO Number"])
                  : "";

                // ✅ Fetch doctor directly from Firestore by National ID
                const foundDoctor =
                  await doctorMasterDataService.getHCPByNationalId(nationalId);
                let doctorMasterDataId = foundDoctor?.id;

                await checkDuplicate({
                  eventId,
                  email,
                  national_id: nationalId,
                  apo_number: apoNumber,
                });

                const attendeeData = {
                  first_name: String(row["First Name"]),
                  family_name: String(row["Family Name"]),
                  national_id: nationalId,
                  email: email,
                  phone_number: String(row["Phone Number"]),
                  country_id: countryId,
                  city_id: cityId,
                  organization: String(row["Organization"]),
                  specialty: String(row["Specialty"]),
                  medical_license: String(row["Medical License"]),
                  APO_number: apoNumber,
                  event_id: eventId,
                  industry_area: row["Industry Area"] || "",
                };

                // ✅ Doctor master data handling
                // if (foundDoctor) {
                //   const hasDoctorChanges = Object.keys(row).some((key) => {
                //     const mappedKey = {
                //       "First Name": "first_name",
                //       "Family Name": "family_name",
                //       "Email Address": "email",
                //       "Phone Number": "phone_number",
                //       Organization: "organization",
                //       Specialty: "specialty",
                //       "APO Number": "APO_number",
                //       "Medical License": "medical_license",
                //       "Industry Area": "industry_area",
                //     }[key];

                //     if (!mappedKey) return false;
                //     return String(row[key]) !== foundDoctor[mappedKey];
                //   });

                //   if (hasDoctorChanges) {
                //     try {
                //       const updatedDoctorData = {
                //         national_id: nationalId,
                //         first_name: attendeeData.first_name,
                //         family_name: attendeeData.family_name,
                //         email: attendeeData.email,
                //         phone_number: attendeeData.phone_number,
                //         country_id: countryId,
                //         city_id: cityId,
                //         organization: attendeeData.organization,
                //         specialty: attendeeData.specialty,
                //         APO_number: attendeeData.APO_number,
                //         medical_license: attendeeData.medical_license,
                //         industry_area: attendeeData.industry_area,
                //       };

                //       await editDoctorMasterData({
                //         id: foundDoctor.id,
                //         data: updatedDoctorData,
                //       });
                //       toast.info(`Updated doctor master data for ${email}`);
                //     } catch (error) {
                //       toast.error(
                //         `Failed to update doctor master data for ${email}`
                //       );
                //       continue;
                //     }
                //   }
                //   doctorMasterDataId = foundDoctor.id;
                // } else {
                // }
                if (!foundDoctor) {
                  try {
                    const doctorData = {
                      national_id: nationalId,
                      first_name: attendeeData.first_name,
                      family_name: attendeeData.family_name,
                      email: attendeeData.email,
                      phone_number: attendeeData.phone_number,
                      country_id: countryId,
                      city_id: cityId,
                      organization: attendeeData.organization,
                      specialty: attendeeData.specialty,
                      APO_number: attendeeData.APO_number,
                      medical_license: attendeeData.medical_license,
                      industry_area: attendeeData.industry_area,
                    };

                    const newDoctorRef = await addDoctorMasterData(doctorData);
                    doctorMasterDataId = newDoctorRef.id;
                    toast.info(`Added new doctor to master data for ${email}`);
                  } catch (error) {
                    toast.error(
                      `Failed to add doctor to master data for ${email}`
                    );
                    continue;
                  }
                }

                attendeeData.doctor_master_data_id = doctorMasterDataId;

                const existingAttendee = eventAttendees?.find(
                  (attendee) =>
                    attendee.email?.toLowerCase() === email?.toLowerCase() ||
                    attendee.national_id === nationalId ||
                    (apoNumber && attendee.APO_number === apoNumber)
                );

                if (existingAttendee) {
                  const hasAttendeeChanges = Object.keys(attendeeData).some(
                    (key) => {
                      if (key === "event_id") return false;
                      if (key === "APO_number") {
                        return (
                          String(attendeeData[key] || "") !==
                          String(existingAttendee[key] || "")
                        );
                      }
                      return (
                        String(attendeeData[key]) !==
                        String(existingAttendee[key])
                      );
                    }
                  );

                  if (hasAttendeeChanges) {
                    try {
                      await editAttendee({
                        id: existingAttendee.id,
                        data: attendeeData,
                      });
                      toast.success(`Updated attendee ${email}`);
                      successCount++;
                    } catch (error) {
                      toast.error(`Failed to update attendee ${email}`);
                      continue;
                    }
                  } else {
                    toast.info(`No changes detected for ${email}`);
                  }
                } else {
                  addAttendee({
                    ...attendeeData,
                    created_at: new Date().toISOString(),
                  });
                  toast.success(`Added new attendee ${email}`);
                  successCount++;
                }
              } catch (error) {
                hasErrors = true;
                toast.error(`Error processing row: ${error.message}`);
              }
            }

            // ✅ Toast after finishing each batch of 10
            toast.success(
              `🎯 Batch ${chunkIndex + 1
              } completed successfully (${successCount} total so far)`
            );

            // Optionally wait a bit between batches (avoid overload)
            await new Promise((resolve) => setTimeout(resolve, 300));
          }

          toast.success(
            `🎉 Import finished! Total successful inserts: ${successCount}`
          );
          event.target.value = "";
          setIsImporting(false);
        };

        reader.readAsArrayBuffer(file);
      } catch (error) {
        toast.error("Error reading file: " + error.message);
        setIsImporting(false);
      }
    }
  };

  return (
    <div className="flex gap-4">
      <button
        className="bg-amber-500 hover:bg-amber-600 transition-colors duration-200 text-white rounded-md px-4 py-2 flex items-center gap-2"
        onClick={handleDownloadTemplate}
      >
        <FileDownloadIcon />
        Download Template
      </button>
      <div>
        <input
          ref={fileInputRef}
          type="file"
          id="import-file"
          accept=".xlsx"
          className="hidden"
          onChange={handleFileChange}
        />
        <label htmlFor="import-file">
          <button
            data-track="import-attendees-button"
            data-module="Attendees"
            onClick={handleClick}
            disabled={isImporting}
            className={`${isImporting
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-green-600 hover:bg-green-700"
              } transition-colors duration-200 text-white rounded-md px-4 py-2 flex items-center gap-2`}
          >
            {isImporting ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <FileUploadIcon />
            )}
            {isImporting ? "Importing..." : "Import Attendees"}
          </button>
        </label>
      </div>
    </div>
  );
}
